import { Component, EventEmitter, Output, inject, OnInit, computed, AfterViewInit, ElementRef, ViewChild, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { Subject, debounceTime, takeUntil } from 'rxjs';

import { ContributeWizardService } from '../../services/contribute-wizard.service';
import { CurrentUserService } from '../../../../shared/services/current-user.service';
import { SafeUrlPipe } from '../../../../shared/pipes/safe-url.pipe';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-step-certificate-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SafeUrlPipe,
  ],
  templateUrl: './step-certificate-details.component.html',
  styleUrl: './step-certificate-details.component.scss',
})
export class StepCertificateDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() back = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();
  @ViewChild('previewContainer') previewContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('previewWrapper') previewWrapper!: ElementRef<HTMLDivElement>;

  protected wizardService = inject(ContributeWizardService);
  private currentUserService = inject(CurrentUserService);
  private transloco = inject(TranslocoService);

  recipientName = '';
  message = '';

  // A4 portrait dimensions in pixels (at 96dpi)
  // 210mm ≈ 794px width, 297mm ≈ 1123px height per page
  private readonly IFRAME_WIDTH_PX = 794;
  private readonly IFRAME_HEIGHT_PX = 2246; // 2 pages: 1123px × 2

  // Debounced signals for preview URL (prevents iframe flashing on every keystroke)
  private debouncedRecipientName = signal('');
  private debouncedMessage = signal('');

  // Subjects for debouncing
  private recipientName$ = new Subject<string>();
  private message$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  private resizeHandler = () => this.updateIframeScale();

  /**
   * Computed URL for the certificate preview iframe.
   * Uses debounced values to prevent iframe flashing on every keystroke.
   */
  previewUrl = computed(() => {
    const project = this.wizardService.selectedProject();
    const params = new URLSearchParams({
      recipientName: this.debouncedRecipientName() || 'Your Name',
      co2Tonnes: this.wizardService.co2ToCompensateTonnes().toFixed(2),
      tripTitle: this.wizardService.trip()?.title || 'Trip',
      locale: this.transloco.getActiveLang().toUpperCase(),
      companyName: this.currentUserService.currentUser()?.companyName || '',
      message: this.debouncedMessage() || '',
    });

    // Pass projectId to load real project icons and equivalences
    if (project?.id) {
      params.set('projectId', project.id);
    } else {
      params.set('projectName', this.getLocalizedProjectName());
    }

    return `${environment.apiUrl}/certificate-preview?${params}`;
  });

  /**
   * Get the project name in the current locale
   */
  private getLocalizedProjectName(): string {
    const project = this.wizardService.selectedProject();
    if (!project) return 'Climate Project';

    const lang = this.transloco.getActiveLang().toUpperCase();
    switch (lang) {
      case 'FR':
        return project.nameFR || project.nameEN;
      case 'NL':
        return project.nameNL || project.nameEN;
      default:
        return project.nameEN;
    }
  }

  ngOnInit(): void {
    // Setup debounced subscriptions for preview URL (500ms delay)
    this.recipientName$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((value) => this.debouncedRecipientName.set(value));

    this.message$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((value) => this.debouncedMessage.set(value));

    // Pre-fill with existing values or company name
    const existingName = this.wizardService.certificateRecipientName();
    if (existingName) {
      this.recipientName = existingName;
      this.debouncedRecipientName.set(existingName); // Initialize immediately
    } else {
      const user = this.currentUserService.currentUser();
      if (user?.companyName) {
        this.recipientName = user.companyName;
        this.debouncedRecipientName.set(user.companyName); // Initialize immediately
        this.wizardService.setCertificateRecipientName(user.companyName);
      }
    }

    this.message = this.wizardService.certificateMessage();
    this.debouncedMessage.set(this.message); // Initialize immediately
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.updateIframeScale();
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Calculate and apply the scale factor for the iframe
   * based on the container's actual width.
   * Also sets the wrapper height to accommodate the scaled 2-page iframe.
   */
  private updateIframeScale(): void {
    if (!this.previewContainer?.nativeElement) return;

    const containerWidth = this.previewContainer.nativeElement.offsetWidth;
    const scale = containerWidth / this.IFRAME_WIDTH_PX;

    // Set scale on iframe
    const iframe = this.previewContainer.nativeElement.querySelector('iframe');
    if (iframe) {
      iframe.style.setProperty('--scale', scale.toString());
    }

    // Set wrapper height to accommodate scaled 2-page iframe
    if (this.previewWrapper?.nativeElement) {
      const scaledHeight = this.IFRAME_HEIGHT_PX * scale;
      this.previewWrapper.nativeElement.style.height = `${scaledHeight}px`;
    }
  }

  /**
   * SECURITY FIX: Sanitize user input to prevent XSS and injection attacks.
   * Removes HTML brackets, control characters, and zero-width characters.
   */
  private sanitizeInput(value: string): string {
    return value
      .replace(/[<>]/g, '') // Remove HTML brackets
      // eslint-disable-next-line no-control-regex -- Intentionally removing control characters for security
      .replace(/[\x00-\x1F\x7F]/g, '') // Remove ASCII control characters
      .replace(/[\u200B-\u200D\uFEFF\u202A-\u202E]/g, '') // Remove zero-width and RTL override chars
      .trim();
  }

  onRecipientNameChange(value: string): void {
    const sanitized = this.sanitizeInput(value);
    if (sanitized.length <= 100) {
      this.wizardService.setCertificateRecipientName(sanitized);
      this.recipientName$.next(sanitized); // Trigger debounced preview update
    }
  }

  onMessageChange(value: string): void {
    const sanitized = this.sanitizeInput(value);
    if (sanitized.length <= 150) {
      this.wizardService.setCertificateMessage(sanitized);
      this.message$.next(sanitized); // Trigger debounced preview update
    } else {
      const truncated = sanitized.substring(0, 150);
      this.message = truncated;
      this.wizardService.setCertificateMessage(truncated);
      this.message$.next(truncated); // Trigger debounced preview update
    }
  }

  onBack(): void {
    this.back.emit();
  }

  onNext(): void {
    if (this.wizardService.canProceedStep3()) {
      this.next.emit();
    }
  }
}
